import React from 'react';
import styles from './style.module.scss';
import ChipList from '@components-clean/core/chip-list';
import { UseStorageStateProps } from '@hooks-clean/index';
import SubjectSelector from '@pages-clean/subject-monitoring/components/subject-selector';
import { useTranslation } from '@hooks/index';

export type SubjectProfileTab = 'timeline' | 'dataListings' | 'tags' | 'notes';

type SubjectProfileTabDefinition = {
    /* For getting translated label. Should be full key, e.g "sm:subjectMonitoringTabs.timeline" */
    translationKey: string;
    value: SubjectProfileTab;
};

const tabs: SubjectProfileTabDefinition[] = [
    {
        translationKey: 'sm:subjectMonitoringTabs.timeline',
        value: 'timeline',
    },
    {
        translationKey: 'sm:subjectMonitoringTabs.dataListings',
        value: 'dataListings',
    },
    {
        translationKey: 'sm:subjectMonitoringTabs.tags',
        value: 'tags',
    },
    {
        translationKey: 'sm:subjectMonitoringTabs.notes',
        value: 'notes',
    },
] as const;

export const SubjectProfileTabStorage: UseStorageStateProps<SubjectProfileTab> = {
    key: 'SM:SubjectProfile.SelectedTab',
    defaultValue: 'timeline',
    parseFn(value: unknown) {
        const tabValues = tabs.map((t) => t.value as string);

        // Check it's a string, and it has same value as the `value` property of one of the `tabs`
        if (typeof value !== 'string' || !tabValues.includes(value)) {
            throw Error(`"${this.key}" does not have a valid value`);
        }

        return value as SubjectProfileTab;
    },
    storageApi: sessionStorage,
};

type Props = {
    selectedTab: SubjectProfileTab;
    setSelectedTab: React.Dispatch<React.SetStateAction<SubjectProfileTab>>;
};

const SubjectProfileActionBar = ({ selectedTab, setSelectedTab }: Props) => {
    const { t } = useTranslation('sm');

    return (
        <div className={styles.subjectProfileActionBar}>
            <SubjectSelector
                containerClassName={styles.subjectProfileSubjectSelectorContainer}
                className={styles.subjectProfileSubjectSelector}
            />

            <ChipList
                className={styles.tabsContainer}
                data-testid="subject-profile-tabs"
                value={selectedTab}
                onChange={(e) => e.value != null && setSelectedTab(e.value as SubjectProfileTab)}
                data={tabs.map((tab) => ({
                    text: t(tab.translationKey),
                    value: tab.value,
                }))}
                ignoreReadonlyContext
            />
        </div>
    );
};

export default SubjectProfileActionBar;
